/**
 * ENG-2259 ULCM: v2 contact API client.
 *
 * Wraps the /api/v2/user/ endpoints. Used by the Account Management Hub
 * and by the <ProposeContactChange /> component.
 *
 * Endpoints documented in pronto-thor/docs/plans/ulcm-planning.md.
 *
 * Notes on failure modes:
 *  - 501 Not Implemented: ULCM_WRITES_ENABLED flag is off in this env.
 *    Callers should render a "feature coming soon" state.
 *  - 409 Conflict: contact is locked (UserContactLockout) or OTP already
 *    consumed. Detail message has specifics.
 *  - 429 Too Many Requests: per-target rate limit tripped.
 *  - 410 Gone: cancellation token expired.
 *  - 400 Bad Request: validation error.
 */

import axios from 'src/utils/axios';
import type { ResendResponse } from 'src/types/otpResend';

const BASE = 'v2/user';

export type CommunicationPreference = 'email' | 'mobile_phone';

export type DeliveryChannel = 'email' | 'sms';

export type ContactType = 'email' | 'phone';

export interface ContactPreferencesPayload {
  communication_preference?: CommunicationPreference;
  sms_opt_in_customer_care?: boolean;
  sms_opt_in_account_notifications?: boolean;
  sms_opt_in_otp_2fa?: boolean;
  receive_renter_notifications?: boolean;
  receive_renter_notifications_email?: boolean;
}

export interface UlcmEmailRow {
  id: number;
  email: string;
  is_primary: boolean;
  verified: boolean;
  // ENG-4115: SES deliverability state for the compliance contact card.
  email_delivery_state?:
    | 'deliverable'
    | 'hard_bounce'
    | 'complaint'
    | 'suppressed'
    | null;
  email_delivery_state_updated_at?: string | null;
  email_delivery_reason?: string | null;
  email_delivery_error_code?: string | null;
}

export interface UlcmPhoneRow {
  id: number;
  phone_number: string;
  is_primary: boolean;
  verified: boolean;
  is_valid?: boolean;
  is_reachable?: boolean;
  opt_out?: boolean;
  // ENG-4114: last Twilio delivery outcome. Drives the SMS capability chip.
  sms_delivery_state?:
    | 'deliverable'
    | 'landline'
    | 'unreachable'
    | 'invalid'
    | 'unsubscribed'
    | 'filtered'
    | 'error'
    | null;
  metadata?: {
    content?: {
      carrier?: {
        type?: string | null;
        name?: string | null;
      };
    };
  } | null;
}

export interface UlcmPendingProposal {
  otp_id: string;
  contact_type: ContactType;
  contact_value: string;
  replace_id?: number | null;
}

export interface AccountStatus {
  is_active: boolean;
  is_locked_unauthorized_cancellation: boolean;
  locked_at: string | null;
  last_cancelled_otp: {
    otp_id: string;
    contact_type: string;
    contact_value: string;
    replace_id: number | null;
    created_at: string;
  } | null;
}

export interface UlcmContactsPayload {
  login_email?: string;
  emails: UlcmEmailRow[];
  phones: UlcmPhoneRow[];
  pending_proposals: UlcmPendingProposal[];
  communication_preference?: CommunicationPreference;
  language_preference?: string;
  account_status?: AccountStatus;
}

export interface ProposeContactResponse extends Partial<ResendResponse> {
  otp_id: string;
  delivery_channel: DeliveryChannel;
  expires_at: string;
}

export interface ConfirmOtpResponse {
  confirmed: boolean;
  attempts_remaining: number;
  is_locked: boolean;
}

class UlcmContactService {
  /**
   * PATCH /api/v2/user/contacts/
   * Updates user-level contact preferences: communication_preference, sms_opt_in_*.
   */
  patchContactPreferences = async (
    payload: ContactPreferencesPayload
  ): Promise<unknown> => {
    const response = await axios.patch(`${BASE}/contacts/`, payload);
    return response.data;
  };

  // ---- Reads ------------------------------------------------------------

  /**
   * GET /api/v2/user/contacts/
   * Returns { emails, phones, communication_preference, language_preference }.
   */
  listContacts = async (asUserParam = ''): Promise<UlcmContactsPayload> => {
    const response = await axios.get(`${BASE}/contacts/${asUserParam}`);
    return response.data;
  };

  /**
   * GET /api/v2/user/data-export/
   * Returns the caller's full ULCM data inventory (DSAR).
   */
  exportData = async (): Promise<unknown> => {
    const response = await axios.get(`${BASE}/data-export/`);
    return response.data;
  };

  // ---- Write (renter self-service) --------------------------------------

  /**
   * POST /api/v2/user/contacts/email/propose/
   */
  proposeEmail = async (
    email: string,
    deliveryChannel?: DeliveryChannel,
    asUserParam = '',
    replaceId?: number | null
  ): Promise<ProposeContactResponse> => {
    const payload: {
      email: string;
      delivery_channel?: DeliveryChannel;
      replace_id?: number;
    } = { email };
    if (deliveryChannel) {
      payload.delivery_channel = deliveryChannel;
    }
    if (replaceId != null) {
      payload.replace_id = replaceId;
    }
    const response = await axios.post(
      `${BASE}/contacts/email/propose/${asUserParam}`,
      payload
    );
    return response.data;
  };

  /**
   * DELETE /api/v2/user/contacts/email/<id>/delete/
   * Tombstones the email. No OTP required.
   */
  deleteEmail = async (id: number, asUserParam = ''): Promise<unknown> => {
    const response = await axios.delete(
      `${BASE}/contacts/email/${id}/delete/${asUserParam}`
    );
    return response.data;
  };

  /**
   * DELETE /api/v2/user/contacts/phone/<id>/delete/
   * Tombstones the phone. No OTP required.
   */
  deletePhone = async (id: number, asUserParam = ''): Promise<unknown> => {
    const response = await axios.delete(
      `${BASE}/contacts/phone/${id}/delete/${asUserParam}`
    );
    return response.data;
  };

  /**
   * PATCH /api/v2/user/contacts/email/<id>/set-primary/
   * Promotes the email to primary.
   */
  setPrimaryEmail = async (id: number, asUserParam = ''): Promise<unknown> => {
    const response = await axios.patch(
      `${BASE}/contacts/email/${id}/set-primary/${asUserParam}`
    );
    return response.data;
  };

  /**
   * PATCH /api/v2/user/contacts/phone/<id>/set-primary/
   * Promotes the phone to primary.
   */
  setPrimaryPhone = async (id: number, asUserParam = ''): Promise<unknown> => {
    const response = await axios.patch(
      `${BASE}/contacts/phone/${id}/set-primary/${asUserParam}`
    );
    return response.data;
  };

  /**
   * POST /api/v2/user/contacts/phone/propose/
   */
  proposePhone = async (
    phoneNumber: string,
    deliveryChannel?: DeliveryChannel,
    asUserParam = '',
    replaceId?: number | null
  ): Promise<ProposeContactResponse> => {
    const payload: {
      phone_number: string;
      delivery_channel?: DeliveryChannel;
      replace_id?: number;
    } = { phone_number: phoneNumber };
    if (deliveryChannel) {
      payload.delivery_channel = deliveryChannel;
    }
    if (replaceId != null) {
      payload.replace_id = replaceId;
    }
    const response = await axios.post(
      `${BASE}/contacts/phone/propose/${asUserParam}`,
      payload
    );
    return response.data;
  };

  /**
   * POST /api/v2/user/contacts/confirm/
   * Returns { confirmed, attempts_remaining, is_locked }.
   *
   * Pass asUserParam (e.g. "?as_user_id=42") when confirming on behalf of a
   * target user so the backend resolves the correct OTP owner.
   */
  confirmOtp = async (
    purpose: string,
    code: string,
    asUserParam = ''
  ): Promise<ConfirmOtpResponse> => {
    const response = await axios.post(
      `${BASE}/contacts/confirm/${asUserParam}`,
      { purpose, code }
    );
    return response.data;
  };

  /**
   * POST /api/v2/user/contacts/propose/{otpId}/cancel/
   * Renter (or helper acting on behalf) cancels an in-flight proposal.
   *
   * Pass asUserParam (e.g. "?as_user_id=42") when cancelling on behalf of a
   * target user so the backend resolves the correct OTP owner.
   */
  cancelOwnProposal = async (
    otpId: string,
    reason?: string,
    asUserParam = ''
  ): Promise<void> => {
    const payload = reason ? { reason } : {};
    await axios.post(
      `${BASE}/contacts/propose/${otpId}/cancel/${asUserParam}`,
      payload
    );
  };

  // ---- Compliance read + propose-on-behalf --------------------------------------

  /**
   * GET /api/v2/user/compliance/users/{userId}/contacts/
   * Returns { emails, phones, pending_proposals } for the target user.
   */
  complianceListContacts = async (
    targetUserId: number | string
  ): Promise<UlcmContactsPayload> => {
    const response = await axios.get(
      `${BASE}/compliance/users/${targetUserId}/contacts/`
    );
    return response.data;
  };

  /**
   * POST /api/v2/user/compliance/users/{userId}/contacts/propose/
   * Requires property-scope on the target user + compliance group.
   *
   * skipSms: when true + contact_type='phone', saves the phone as a
   *   record-only contact (verified=false) without dispatching an OTP.
   *   Only effective when Twilio Lookup confirms the number is valid but
   *   not SMS-capable; any other case is rejected with 4xx.
   */
  complianceProposeOnBehalf = async (
    targetUserId: number | string,
    contactType: ContactType,
    contactValue: string,
    note?: string,
    replaceId?: number | null,
    skipSms?: boolean
  ): Promise<ProposeContactResponse> => {
    const payload: {
      contact_type: ContactType;
      contact_value: string;
      note?: string;
      replace_id?: number;
      skip_sms?: boolean;
    } = {
      contact_type: contactType,
      contact_value: contactValue,
    };
    if (note) {
      payload.note = note;
    }
    if (replaceId != null) {
      payload.replace_id = replaceId;
    }
    if (skipSms) {
      payload.skip_sms = true;
    }
    const response = await axios.post(
      `${BASE}/compliance/users/${targetUserId}/contacts/propose/`,
      payload
    );
    return response.data;
  };

  /**
   * POST /api/v2/user/compliance/users/{userId}/contacts/proposals/{otpId}/cancel/
   * Cancels an in-flight contact_change proposal for the target renter.
   */
  complianceCancelProposal = async (
    targetUserId: number | string,
    otpId: string,
    reason?: string
  ): Promise<void> => {
    const payload = reason ? { reason } : {};
    await axios.post(
      `${BASE}/compliance/users/${targetUserId}/contacts/proposals/${otpId}/cancel/`,
      payload
    );
  };

  /**
   * POST /api/v2/user/compliance/users/{userId}/contacts/unlock/
   * Unlocks a renter account locked by unauthorized_cancellation and
   * re-sends the verification code + security notification.
   */
  complianceUnlockAccount = async (
    targetUserId: number | string,
    otpId: string
  ): Promise<{
    unlocked: boolean;
    otp_id: string;
    contact_type: string;
    masked_contact_value: string;
  }> => {
    const response = await axios.post(
      `${BASE}/compliance/users/${targetUserId}/contacts/unlock/`,
      { otp_id: otpId }
    );
    return response.data;
  };

  /**
   * Convenience wrapper: compliance resends a proposal by re-issuing one
   * with the same contact_value + replace_id. The backend supersedes the
   * previous OTP automatically.
   */
  complianceResendProposal = async (
    targetUserId: number | string,
    contactType: ContactType,
    contactValue: string,
    replaceId?: number | null
  ): Promise<ProposeContactResponse> =>
    this.complianceProposeOnBehalf(
      targetUserId,
      contactType,
      contactValue,
      undefined,
      replaceId,
      false
    );
}

const ulcmContactService = new UlcmContactService();

export default ulcmContactService;
